import { deepmerge, Theme } from '@biome-design-system/styles';
import { fromTheme as kyruusFromTheme } from '@kyruus/ui-theme';
import { buildPalette } from './palette';
import { buildTypography } from './typography';
import { buildComponents } from './components';

/**
 * Converts a pixel value string (e.g. "16px") to a number.
 * @param {string} key - The pixel value string to convert.
 * @returns {number} The numeric value of the pixel string.
 */
const convertToNumber = (key: string) => parseInt(key.replace('px', ''), 10);

/**
 * Builds a custom MUI theme by merging Kyruus and Biome theme objects.
 * Applies palette, typography, breakpoints, shape, and component overrides.
 * @param {object} kyruusTheme - The Kyruus theme object
 * @param {object} biomeTheme - The Biome theme object (which is a MUI theme)
 * @returns {object} The merged MUI theme object, ready for use with ThemeProvider.
 *
 * This function extracts color, typography, and layout values from the Kyruus theme,
 * constructs a MUI-compatible theme structure, and merges it with the Biome theme.
 * It also applies custom component overrides, such as for MUI Button, to ensure
 * consistent styling across the application.
 */
export const buildKyruusBiomeTheme = (
  kyruusTheme: Record<string, unknown>,
  biomeTheme: Theme
) => {
  // wrap fromTheme to simplify the code below
  const fromTheme = (themeKey: string) =>
    kyruusFromTheme(themeKey)({ theme: kyruusTheme });

  const borderRadius = fromTheme('border_radius');

  return deepmerge(biomeTheme, {
    // primitives (base styles) that are applied within biome components (taken from createMuiThemeFromEmotionTheme in kyruus-components)
    breakpoints: {
      values: {
        xs: 0,
        sm: convertToNumber(fromTheme('screen_small')),
        md: convertToNumber(fromTheme('screen_medium')),
        lg: convertToNumber(fromTheme('screen_large')),
        xl: convertToNumber(fromTheme('screen_large'))
      },
      unit: 'px',
      step: 5
    },
    // individual component overrides should go into their own functions to keep this manageable/easy to read
    components: buildComponents(kyruusTheme, biomeTheme),
    palette: buildPalette(kyruusTheme),
    shape: { borderRadius: borderRadius },
    typography: buildTypography(kyruusTheme)
  });
};
